import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Ship,
  Building2,
  Package,
  MapPin,
  Plus,
  Trash2,
  Info,
  Send
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { SmartLocationInput } from "../components/SmartLocationInput";
import { API_URL } from "../config/api";


export default function BLInstructionsPage() {
  const { pfId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [pfData, setPfData] = useState(null);
  
  // BL Type selection
  const [blType, setBlType] = useState("hbl_only"); // "hbl_only" or "mbl_and_hbl"
  
  // Form state
  const [formData, setFormData] = useState({
    // Shipper/Exporter
    shipper_name: "",
    shipper_address: "",
    shipper_city: "",
    shipper_country: "",
    shipper_contact: "",
    shipper_phone: "",
    shipper_email: "",
    
    // Consignee
    consignee_name: "",
    consignee_address: "",
    consignee_city: "",
    consignee_country: "",
    consignee_contact: "",
    consignee_phone: "",
    consignee_email: "",
    
    // Notify Party
    notify_same_as_consignee: false,
    notify_name: "",
    notify_address: "",
    notify_city: "",
    notify_country: "",
    notify_contact: "",
    notify_phone: "",
    
    // Shipment Details
    vessel_name: "",
    voyage_number: "",
    port_of_loading: "",
    port_of_discharge: "",
    place_of_receipt: "",
    place_of_delivery: "",
    
    // Cargo Lines
    cargo_lines: [{
      container_number: "",
      seal_number: "",
      marks_numbers: "",
      package_count: "",
      package_type: "CARTONS",
      description: "",
      gross_weight_kg: "",
      volume_cbm: "",
      hs_code: ""
    }],
    
    // Additional Info
    freight_terms: "PREPAID", // PREPAID or COLLECT
    incoterm: "FOB",
    declared_value: "",
    special_instructions: "",
    
    // MBL specific (only if mbl_and_hbl)
    mbl_shipper_name: "",
    mbl_shipper_address: "",
    mbl_consignee_name: "",
    mbl_consignee_address: "",
    mbl_notify_name: "",
    mbl_notify_address: "",
  });

  // Fetch PF data to pre-fill some fields
  useEffect(() => {
    const fetchPFData = async () => {
      try {
        const response = await fetch(
          `${API_URL}/bl-instructions/${pfId}/${token}/info`
        );
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || "Invalid or expired link");
        }
        
        const data = await response.json();
        setPfData(data);
        
        // Pre-fill some fields from PF data
        setFormData(prev => ({
          ...prev,
          port_of_loading: data.origin_port || "",
          port_of_discharge: data.destination_port || "",
          vessel_name: data.vessel || "",
        }));
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPFData();
  }, [pfId, token]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addCargoLine = () => {
    setFormData(prev => ({
      ...prev,
      cargo_lines: [...prev.cargo_lines, {
        container_number: "",
        seal_number: "",
        marks_numbers: "",
        package_count: "",
        package_type: "CARTONS",
        description: "",
        gross_weight_kg: "",
        volume_cbm: "",
        hs_code: ""
      }]
    }));
  };

  const removeCargoLine = (index) => {
    if (formData.cargo_lines.length > 1) {
      setFormData(prev => ({
        ...prev,
        cargo_lines: prev.cargo_lines.filter((_, i) => i !== index)
      }));
    }
  };

  const updateCargoLine = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      cargo_lines: prev.cargo_lines.map((line, i) => 
        i === index ? { ...line, [field]: value } : line
      )
    }));
  };

  const handleNotifySameAsConsignee = (checked) => {
    updateField("notify_same_as_consignee", checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        notify_name: prev.consignee_name,
        notify_address: prev.consignee_address,
        notify_city: prev.consignee_city,
        notify_country: prev.consignee_country,
        notify_contact: prev.consignee_contact,
        notify_phone: prev.consignee_phone,
      }));
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.shipper_name || !formData.consignee_name) {
      toast.error("Please fill in shipper and consignee details");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/bl-instructions/${pfId}/${token}/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bl_type: blType,
            ...formData
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to submit BL instructions");
      }

      setSubmitted(true);
      toast.success("BL Instructions submitted successfully!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950 flex items-center justify-center p-4">
        <Loader2 className="w-12 h-12 text-white animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Link Invalid</h2>
          <p className="text-gray-600">{error}</p>
        </motion.div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-green-700 mb-2">BL Instructions Submitted!</h2>
          <p className="text-gray-600 mb-4">
            Thank you for submitting your Bill of Lading instructions. Our team will process them shortly.
          </p>
          <p className="text-sm text-gray-500">
            Reference: {pfData?.pf_number}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white">Europacific</h1>
              <p className="text-white/60 text-sm">Bill of Lading Instructions</p>
            </div>
            <div className="text-right">
              <p className="text-white/60 text-xs">Reference</p>
              <p className="text-white font-mono font-semibold">{pfData?.pf_number}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-6"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Submit Your BL Instructions</h2>
              <p className="text-gray-600">
                Please provide the details for your Bill of Lading. This information will be used to 
                prepare your shipping documents. Ensure all details are accurate to avoid delays.
              </p>
            </div>
          </div>
        </motion.div>

        {/* BL Type Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Ship className="w-5 h-5 text-blue-600" />
            Bill of Lading Type
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label 
              className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                blType === "hbl_only" 
                  ? "border-blue-500 bg-blue-50" 
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="blType"
                value="hbl_only"
                checked={blType === "hbl_only"}
                onChange={(e) => setBlType(e.target.value)}
                className="sr-only"
              />
              <div className="font-semibold text-gray-900">House BL Only (HBL)</div>
              <p className="text-sm text-gray-600 mt-1">
                Europacific issues the Bill of Lading. Suitable when we handle your cargo consolidation.
              </p>
            </label>
            <label 
              className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                blType === "mbl_and_hbl" 
                  ? "border-blue-500 bg-blue-50" 
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="blType"
                value="mbl_and_hbl"
                checked={blType === "mbl_and_hbl"}
                onChange={(e) => setBlType(e.target.value)}
                className="sr-only"
              />
              <div className="font-semibold text-gray-900">Master BL + House BL</div>
              <p className="text-sm text-gray-600 mt-1">
                Shipping line issues MBL, Europacific issues HBL. For FCL shipments with carrier BL required.
              </p>
            </label>
          </div>
        </motion.div>

        {/* Shipper Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-green-600" />
            Shipper / Exporter Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.shipper_name}
                onChange={(e) => updateField("shipper_name", e.target.value)}
                placeholder="Full legal company name"
                data-testid="shipper-name"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Address *</Label>
              <Textarea
                value={formData.shipper_address}
                onChange={(e) => updateField("shipper_address", e.target.value)}
                placeholder="Street address"
                rows={2}
              />
            </div>
            <div>
              <Label>City</Label>
              <Input
                value={formData.shipper_city}
                onChange={(e) => updateField("shipper_city", e.target.value)}
                placeholder="City"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Input
                value={formData.shipper_country}
                onChange={(e) => updateField("shipper_country", e.target.value)}
                placeholder="Country"
              />
            </div>
            <div>
              <Label>Contact Person</Label>
              <Input
                value={formData.shipper_contact}
                onChange={(e) => updateField("shipper_contact", e.target.value)}
                placeholder="Contact name"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.shipper_phone}
                onChange={(e) => updateField("shipper_phone", e.target.value)}
                placeholder="+1 234 567 890"
              />
            </div>
          </div>
        </motion.div>

        {/* Consignee Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            Consignee Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.consignee_name}
                onChange={(e) => updateField("consignee_name", e.target.value)}
                placeholder="Full legal company name"
                data-testid="consignee-name"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Address *</Label>
              <Textarea
                value={formData.consignee_address}
                onChange={(e) => updateField("consignee_address", e.target.value)}
                placeholder="Street address"
                rows={2}
              />
            </div>
            <div>
              <Label>City</Label>
              <Input
                value={formData.consignee_city}
                onChange={(e) => updateField("consignee_city", e.target.value)}
                placeholder="City"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Input
                value={formData.consignee_country}
                onChange={(e) => updateField("consignee_country", e.target.value)}
                placeholder="Country"
              />
            </div>
            <div>
              <Label>Contact Person</Label>
              <Input
                value={formData.consignee_contact}
                onChange={(e) => updateField("consignee_contact", e.target.value)}
                placeholder="Contact name"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.consignee_phone}
                onChange={(e) => updateField("consignee_phone", e.target.value)}
                placeholder="+1 234 567 890"
              />
            </div>
          </div>
        </motion.div>

        {/* Notify Party */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-orange-600" />
            Notify Party
          </h3>
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.notify_same_as_consignee}
                onChange={(e) => handleNotifySameAsConsignee(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Same as Consignee</span>
            </label>
          </div>
          {!formData.notify_same_as_consignee && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Company Name</Label>
                <Input
                  value={formData.notify_name}
                  onChange={(e) => updateField("notify_name", e.target.value)}
                  placeholder="Notify party name"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Textarea
                  value={formData.notify_address}
                  onChange={(e) => updateField("notify_address", e.target.value)}
                  placeholder="Street address"
                  rows={2}
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={formData.notify_city}
                  onChange={(e) => updateField("notify_city", e.target.value)}
                  placeholder="City"
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={formData.notify_country}
                  onChange={(e) => updateField("notify_country", e.target.value)}
                  placeholder="Country"
                />
              </div>
            </div>
          )}
        </motion.div>

        {/* MBL Details - Only if mbl_and_hbl selected */}
        {blType === "mbl_and_hbl" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-2xl shadow-xl p-6 mb-6"
          >
            <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <Info className="w-5 h-5" />
              Master BL Details (For Shipping Line)
            </h3>
            <p className="text-sm text-amber-700 mb-4">
              These details will be used for the carrier's Master Bill of Lading. 
              If different from above, please specify.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>MBL Shipper Name</Label>
                <Input
                  value={formData.mbl_shipper_name}
                  onChange={(e) => updateField("mbl_shipper_name", e.target.value)}
                  placeholder="Leave blank if same as HBL shipper"
                />
              </div>
              <div>
                <Label>MBL Shipper Address</Label>
                <Input
                  value={formData.mbl_shipper_address}
                  onChange={(e) => updateField("mbl_shipper_address", e.target.value)}
                  placeholder="Leave blank if same"
                />
              </div>
              <div>
                <Label>MBL Consignee</Label>
                <Input
                  value={formData.mbl_consignee_name}
                  onChange={(e) => updateField("mbl_consignee_name", e.target.value)}
                  placeholder="Often: TO ORDER or Europacific"
                />
              </div>
              <div>
                <Label>MBL Notify Party</Label>
                <Input
                  value={formData.mbl_notify_name}
                  onChange={(e) => updateField("mbl_notify_name", e.target.value)}
                  placeholder="MBL notify party"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Shipment Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Shipment Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Vessel Name</Label>
              <Input
                value={formData.vessel_name}
                onChange={(e) => updateField("vessel_name", e.target.value)}
                placeholder="Vessel name"
              />
            </div>
            <div>
              <Label>Voyage Number</Label>
              <Input
                value={formData.voyage_number}
                onChange={(e) => updateField("voyage_number", e.target.value)}
                placeholder="Voyage number"
              />
            </div>
            <div>
              <Label className="mb-1 block">Port of Loading</Label>
              <SmartLocationInput
                value={{
                  city: formData.port_of_loading,
                  country: formData.pol_country || "",
                  port_code: formData.pol_code || "",
                  port_name: formData.port_of_loading,
                }}
                onChange={(loc) => {
                  updateField("port_of_loading", loc.city || loc.port_name || "");
                  updateField("pol_code", loc.port_code || "");
                  updateField("pol_country", loc.country || "");
                }}
                showSeaport={true}
                showAirport={false}
                placeholder="Search port..."
                data-testid="bl-pol"
              />
            </div>
            <div>
              <Label className="mb-1 block">Port of Discharge</Label>
              <SmartLocationInput
                value={{
                  city: formData.port_of_discharge,
                  country: formData.pod_country || "",
                  port_code: formData.pod_code || "",
                  port_name: formData.port_of_discharge,
                }}
                onChange={(loc) => {
                  updateField("port_of_discharge", loc.city || loc.port_name || "");
                  updateField("pod_code", loc.port_code || "");
                  updateField("pod_country", loc.country || "");
                }}
                showSeaport={true}
                showAirport={false}
                placeholder="Search port..."
                data-testid="bl-pod"
              />
            </div>
            <div>
              <Label className="mb-1 block">Place of Receipt</Label>
              <SmartLocationInput
                value={{
                  city: formData.place_of_receipt,
                  country: formData.por_country || "",
                  port_code: formData.por_code || "",
                  port_name: formData.place_of_receipt,
                }}
                onChange={(loc) => {
                  updateField("place_of_receipt", loc.city || loc.port_name || "");
                  updateField("por_code", loc.port_code || "");
                  updateField("por_country", loc.country || "");
                }}
                showSeaport={true}
                showAirport={false}
                placeholder="If different from POL..."
                data-testid="bl-por"
              />
            </div>
            <div>
              <Label className="mb-1 block">Place of Delivery</Label>
              <SmartLocationInput
                value={{
                  city: formData.place_of_delivery,
                  country: formData.pod_delivery_country || "",
                  port_code: formData.pod_delivery_code || "",
                  port_name: formData.place_of_delivery,
                }}
                onChange={(loc) => {
                  updateField("place_of_delivery", loc.city || loc.port_name || "");
                  updateField("pod_delivery_code", loc.port_code || "");
                  updateField("pod_delivery_country", loc.country || "");
                }}
                showSeaport={true}
                showAirport={false}
                placeholder="Final destination..."
                data-testid="bl-pod-delivery"
              />
            </div>
            <div>
              <Label>Freight Terms</Label>
              <select
                value={formData.freight_terms}
                onChange={(e) => updateField("freight_terms", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
              >
                <option value="PREPAID">Freight Prepaid</option>
                <option value="COLLECT">Freight Collect</option>
              </select>
            </div>
            <div>
              <Label>Incoterm</Label>
              <select
                value={formData.incoterm}
                onChange={(e) => updateField("incoterm", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
              >
                <option value="EXW">EXW - Ex Works</option>
                <option value="FCA">FCA - Free Carrier</option>
                <option value="FOB">FOB - Free On Board</option>
                <option value="CFR">CFR - Cost and Freight</option>
                <option value="CIF">CIF - Cost, Insurance & Freight</option>
                <option value="DAP">DAP - Delivered at Place</option>
                <option value="DDP">DDP - Delivered Duty Paid</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Cargo Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-600" />
              Cargo Details
            </h3>
            <Button size="sm" variant="outline" onClick={addCargoLine}>
              <Plus className="w-4 h-4 mr-1" />
              Add Container
            </Button>
          </div>
          
          {formData.cargo_lines.map((line, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-gray-700">Container {index + 1}</span>
                {formData.cargo_lines.length > 1 && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => removeCargoLine(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Container No.</Label>
                  <Input
                    value={line.container_number}
                    onChange={(e) => updateCargoLine(index, "container_number", e.target.value)}
                    placeholder="ABCD1234567"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Seal No.</Label>
                  <Input
                    value={line.seal_number}
                    onChange={(e) => updateCargoLine(index, "seal_number", e.target.value)}
                    placeholder="Seal number"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Packages</Label>
                  <Input
                    value={line.package_count}
                    onChange={(e) => updateCargoLine(index, "package_count", e.target.value)}
                    placeholder="100"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Package Type</Label>
                  <select
                    value={line.package_type}
                    onChange={(e) => updateCargoLine(index, "package_type", e.target.value)}
                    className="w-full h-9 px-2 text-sm rounded-md border border-input bg-background"
                  >
                    <option value="CARTONS">Cartons</option>
                    <option value="PALLETS">Pallets</option>
                    <option value="PACKAGES">Packages</option>
                    <option value="BAGS">Bags</option>
                    <option value="DRUMS">Drums</option>
                    <option value="PIECES">Pieces</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Marks & Numbers</Label>
                  <Input
                    value={line.marks_numbers}
                    onChange={(e) => updateCargoLine(index, "marks_numbers", e.target.value)}
                    placeholder="Shipping marks"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Gross Weight (KG)</Label>
                  <Input
                    value={line.gross_weight_kg}
                    onChange={(e) => updateCargoLine(index, "gross_weight_kg", e.target.value)}
                    placeholder="15000"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Volume (CBM)</Label>
                  <Input
                    value={line.volume_cbm}
                    onChange={(e) => updateCargoLine(index, "volume_cbm", e.target.value)}
                    placeholder="55"
                    className="text-sm"
                  />
                </div>
                <div className="col-span-2 md:col-span-4">
                  <Label className="text-xs">Description of Goods *</Label>
                  <Textarea
                    value={line.description}
                    onChange={(e) => updateCargoLine(index, "description", e.target.value)}
                    placeholder="Detailed description of goods"
                    rows={2}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">HS Code</Label>
                  <Input
                    value={line.hs_code}
                    onChange={(e) => updateCargoLine(index, "hs_code", e.target.value)}
                    placeholder="8471.30.00"
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Special Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4">Special Instructions</h3>
          <Textarea
            value={formData.special_instructions}
            onChange={(e) => updateField("special_instructions", e.target.value)}
            placeholder="Any special instructions or additional information for the BL..."
            rows={3}
          />
        </motion.div>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="flex justify-center mb-8"
        >
          <Button 
            size="lg" 
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-700 px-8"
            data-testid="submit-bl-instructions-btn"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Send className="w-5 h-5 mr-2" />
            )}
            {submitting ? "Submitting..." : "Submit BL Instructions"}
          </Button>
        </motion.div>

        {/* Footer */}
        <div className="text-center text-white/50 text-xs pb-8">
          <p>Europacific Logistika d.o.o.</p>
          <p>info@europacific.com | +386 5 662 6380</p>
        </div>
      </div>
    </div>
  );
}
